import { periverseTheme } from '@peritasai/ui-shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useLayoutEffect } from 'react';
import { useAuthContext } from '../features/auth';
import { getLandingPath } from '../shared/utils/landing-path';

// Module-level flag to prevent double login (React StrictMode double-invokes effects).
// A ref would reset on remount, allowing a second login() to overwrite pkce_code_verifier
// and cause 401 on token exchange.
let hasInitiatedLogin = false;

export const Route = createFileRoute('/')({
  component: RootIndex,
});

function AuthLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ backgroundColor: periverseTheme.palette.background.default }}
    >
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );
}

function RootIndex() {
  const { isLoading, isAuthenticated, isInitialized, login, user } =
    useAuthContext();
  const navigate = useNavigate();

  // When authenticated, push to the landing page synchronously before paint so
  // the browser back-button cannot land on this intermediate route and expose
  // the Zitadel login page that lives behind it in history.
  useLayoutEffect(() => {
    if (!isInitialized || !isAuthenticated || !user) return;
    navigate({ to: getLandingPath(user), replace: true });
  }, [isAuthenticated, isInitialized, navigate, user]);

  // Auto-redirect to Zitadel when unauthenticated
  useEffect(() => {
    // Reset when authenticated so retry works after logout
    if (isAuthenticated) {
      hasInitiatedLogin = false;
      return;
    }
    if (!isInitialized || isLoading || hasInitiatedLogin) return;

    hasInitiatedLogin = true;
    login().catch(() => {
      hasInitiatedLogin = false;
    });
  }, [isAuthenticated, isInitialized, isLoading, login]);

  // Show loading state while checking authentication
  if (isLoading || !isInitialized) {
    return <AuthLoader />;
  }

  // Show loader while redirecting to Zitadel (login() triggers full-page redirect)
  if (!isAuthenticated) {
    return <AuthLoader />;
  }

  // Render nothing while useLayoutEffect fires the push navigation
  return null;
}
